import React, { ElementType, ReactNode } from 'react';
import Box from '../Box';
import { IconArrowRight24 } from '../icons';
import { ResponsiveSizeValue } from '../_internal/styling';
import Stack from '../Stack';
import StickerLabel, { StickerLabelProps } from '../StickerLabel';
import Text, { TextProps } from '../Text';

import styles from './moduleHeader.module.scss';

export const STANDARD_MARGIN_BOTTOM = { sm: 1, md: 1.5 };

export interface ModuleHeaderProps {
  /**
   * The header text.
   */
  header: ReactNode;

  /**
   * The subheader text.
   */
  subheader?: ReactNode;

  /**
   * Optional badge that displays to the right of the header text.
   * Example "New"
   */
  stickerLabel?: {
    text: ReactNode;
    variant?: StickerLabelProps['variant'];
  };

  /**
   * Text displays as link on small and larger screens.
   * On x-small screens, the cta.text is hidden and the entire
   * header becomes clickable. The `cta.linkAs` can be used to
   * set a custom link element type like NextLink (default 'a').
   */
  cta?: {
    linkAs?: 'a' | ElementType;
    onClick?: () => void;
    text: ReactNode;
    url: string;
  };

  /**
   * Optional margin bottom on the ModuleHeader. Standard default is
   * 1rem on small screens and 2rem on larger screens before the rest of
   * the content underneath.
   */
  mb?: 'default' | ResponsiveSizeValue;
}

/**
 * Renders the header text. If a CTA is provided, the header text will be a link.
 */
const renderHeader = (
  cta: ModuleHeaderProps['cta'],
  header: ModuleHeaderProps['header'],
  setting: TextProps['setting'],
) => {
  if (!cta) {
    return <Text setting={setting}>{header}</Text>;
  }

  return (
    <Text
      as={cta.linkAs || 'a'}
      setting={setting}
      href={cta?.url}
      className={styles['header-link']}
      onClick={cta.onClick}
      data-testid="module-header-link"
    >
      {header}
      <IconArrowRight24
        purpose="decorative"
        className={styles['icon-button']}
      />
    </Text>
  );
};

/**
 * Calculates the margin bottom based on the `mb` prop. If default prop is passed in then
 * we standardize the margin bottom to 1rem on small screens and 2rem on larger screens.
 */
const calculateMarginBottom = (mb: ModuleHeaderProps['mb']) => {
  if (mb === 'default' || mb === undefined) {
    return STANDARD_MARGIN_BOTTOM;
  }

  return mb;
};

/**
 * A reusable section header. Supports optional subheader, stickerLabel, and CTA.
 */
const ModuleHeader = ({
  header,
  subheader,
  stickerLabel,
  cta,
  mb,
}: ModuleHeaderProps) => {
  const marginBottom = calculateMarginBottom(mb);

  return (
    <Stack spacing={0.25} mb={marginBottom} data-testid="module-header">
      {stickerLabel && (
        <StickerLabel variant={stickerLabel.variant}>
          {stickerLabel.text}
        </StickerLabel>
      )}
      <Box>{renderHeader(cta, header, 'title-medium')}</Box>
      {subheader && (
        <Text mt={0.25} setting="body-medium">
          {subheader}
        </Text>
      )}
    </Stack>
  );
};

export default ModuleHeader;
